<template>
  <v-dialog :value="value" max-width="1000" scrollable persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        {{ formTitle }}
      </v-card-title>

      <v-card-text>
        <ValidationObserver ref="formObserver">
          <v-form :disabled="isLoading">
            <v-container>
              <v-row>
                <v-col cols="12" sm="6">
                  <vee-text-field v-model="assessment.name" rules="required|max:128" label="Name*" />
                </v-col>

                <v-col cols="12" sm="3">
                  <v-checkbox v-model="assessment.active" label="Active" />
                </v-col>

                <v-col cols="12" sm="3">
                  <vee-select
                    v-model="assessment.groupType"
                    :items="flowGroupTypeOptions"
                    label="Type"
                    clearable
                  />
                </v-col>

                <v-col cols="12">
                  <vee-assessment-autocomplete
                    v-model="assessment.exercises"
                    :loading="areFitnessAssessmentsLoading"
                    :items="filteredExercises"
                    label="Assessments*"
                    rules="required|arrayRequired"
                    name="exercises"
                    :error-messages="showSessionsError ? ['Required'] : []"
                    :menu-props="{ maxHeight: '400px', closeOnContentClick: false }"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </ValidationObserver>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="onCancel">
          Cancel
        </v-btn>
        <v-btn text :loading="isLoading" @click="onSave">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  computed, onMounted, ref, nextTick,
} from 'vue'
import { ValidationObserver } from 'vee-validate'
import { ExerciseCategory, FlowExercisesType } from '@/enums/enums'
import useFitnessAssessments from '@/composables/api/assessments/fitness'
import VeeAssessmentAutocomplete from '@/components/veeInputs/VeeAssessmentAutocomplete.vue'

export default {
  components: {
    ValidationObserver,
    VeeAssessmentAutocomplete,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      required: true,
      type: Object,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { fitnessAssessments, isLoading: areFitnessAssessmentsLoading, fetchFitnessAssessments } = useFitnessAssessments('/assessment/flow')

    const assessment = ref({
      exercises: [],
      category: ExerciseCategory.FLOW,
      groupType: '',
      ...props.item,
    })

    const formTitle = computed(() => {
      if (!assessment.value.id || assessment.value.id === '') {
        return 'Create Flow Assessment'
      }

      return 'Edit Flow Assessment'
    })

    const isFlowAssessment = item => Boolean(
      item?.questionnaire
        || item?.exercise?.category === ExerciseCategory.FLOW
        || Object.values(FlowExercisesType).includes(item?.sessionType),
    )

    const filteredExercises = computed(() => {
      const byUniqueKey = new Map()
      const sourceItems = [
        ...(fitnessAssessments.value || []).filter(isFlowAssessment),
        ...(assessment.value.exercises || []),
      ]

      sourceItems
        .filter(item => item && typeof item === 'object' && item.uniqueKey)
        .forEach(item => {
          byUniqueKey.set(item.uniqueKey, {
            ...byUniqueKey.get(item.uniqueKey),
            ...item,
          })
        })

      return Array.from(byUniqueKey.values())
    })

    const flowGroupTypeOptions = computed(() => [
      { text: 'Quiet Mind', value: FlowExercisesType.QUIET_MIND },
      { text: 'Breathing', value: FlowExercisesType.BREATHING },
      { text: 'Connection', value: FlowExercisesType.CONNECTION },
      { text: 'Preparation', value: FlowExercisesType.PREPARATION },
      { text: 'Commitment', value: FlowExercisesType.COMMITMENT },
      { text: 'Pressure', value: FlowExercisesType.PRESSURE },
    ])

    const formObserver = ref(null)

    const buildAssessmentPayload = formAssessment => {
      const grouped = (formAssessment.exercises || [])
        .filter(item => item && item.sessionType && item.uniqueKey)
        .reduce((acc, item) => {
          const { sessionType } = item
          if (!acc[sessionType]) {
            acc[sessionType] = {
              category: ExerciseCategory.FLOW,
              type: sessionType,
              assessments: [],
            }
          }
          acc[sessionType].assessments.push({ ...item })

          return acc
        }, {})

      return {
        id: formAssessment.id,
        name: formAssessment.name,
        category: ExerciseCategory.FLOW,
        groupType: formAssessment.groupType || '',
        companyId: formAssessment.companyId || null,
        sessions: Object.values(grouped),
        userId: formAssessment.userId || null,
        active: formAssessment.active ?? true,
      }
    }

    const sessions = computed(() => buildAssessmentPayload(assessment.value).sessions || [])
    const showSessionsError = ref(false)

    onMounted(async () => {
      await fetchFitnessAssessments({ active: null, includeInactive: true, withInactive: true })

      let extractedExercises = []

      if (props.item.sessions && Array.isArray(props.item.sessions)) {
        extractedExercises = props.item.sessions.flatMap(s => {
          if (s.assessments && Array.isArray(s.assessments)) return s.assessments
          if (s.exercises && Array.isArray(s.exercises)) return s.exercises
          if (s.uniqueKey) return [s]

          return []
        })
      } else if (props.item.exercises && Array.isArray(props.item.exercises)) {
        extractedExercises = props.item.exercises
      }

      let mappedExercises = []
      if (extractedExercises.length > 0) {
        const allExercises = fitnessAssessments.value || []
        mappedExercises = extractedExercises.map(existing => {
          const uKey = typeof existing === 'string' ? existing : existing.uniqueKey
          const found = allExercises.find(e => e.uniqueKey === uKey)

          return found ? { ...found, ...(typeof existing === 'object' ? existing : {}) } : existing
        }).filter(e => e && typeof e === 'object' && e.uniqueKey)
      }

      assessment.value = {
        ...assessment.value,
        active: assessment.value.active !== undefined ? assessment.value.active : true,
        category: ExerciseCategory.FLOW,
        exercises: mappedExercises,
      }
    })

    const onCancel = () => {
      emit('cancel')
    }

    const onSave = async () => {
      if (!assessment.value.exercises) {
        assessment.value.exercises = []
      }
      await nextTick()
      const isValid = await formObserver.value.validate()
      if (isValid && sessions.value.length > 0) {
        showSessionsError.value = false
        const payload = buildAssessmentPayload(assessment.value)
        emit('save', payload)
      } else if (sessions.value.length === 0) {
        showSessionsError.value = true
      }
    }

    return {
      formObserver,
      assessment,
      areFitnessAssessmentsLoading,
      filteredExercises,
      flowGroupTypeOptions,
      onCancel,
      onSave,
      formTitle,
      showSessionsError,
      sessions,
    }
  },
}
</script>
